import { Box, DialogActions, DialogContent, Stack, TextField } from '@mui/material';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { useAppTFunction } from '@retail/app/i18n';
import { StackedTextCell } from '@retail/components';
import { PriceSuggestionPriceItem } from '@retail/price-suggestion/types';
import { ContainedButton, OutlinedButton, TextButton } from '@shared/components';
import { DateSelectField } from '@retail/products/components';
import { useCallback, useMemo } from 'react';
import {
  addPriceSuggestionPricesSchema,
  AddPriceSuggestionPricesSchema,
  PriceSuggestionPricesSchema,
} from '../';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

const baseColumnProps: Partial<GridColDef> = {
  width: 180,
  sortable: false,
  headerAlign: 'left',
  align: 'left',
};

interface Props {
  priceSuggestionPriceItems: PriceSuggestionPriceItem[];
  setPriceSuggestionPriceItems: (items: PriceSuggestionPriceItem[]) => void;
  goBack?: () => void;
  onClose: () => void;
  handleSubmitPrices: () => void;
}

export function EditPricesForSelectedItems({
  priceSuggestionPriceItems,
  setPriceSuggestionPriceItems,
  goBack,
  onClose,
  handleSubmitPrices,
}: Props) {
  const t = useAppTFunction();

  const priceSuggestionItems: AddPriceSuggestionPricesSchema['items'] =
    priceSuggestionPriceItems.reduce(
      (prev, item) => ({
        ...prev,
        [item.mgSupplierItemId + '']: {
          salesPrice: item.price ?? null,
          fromDate: item.validFrom ?? new Date(),
          toDate: item.validTo,
        } as PriceSuggestionPricesSchema,
      }),
      {}
    );

  const {
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<AddPriceSuggestionPricesSchema>({
    mode: 'onSubmit',
    defaultValues: {
      items: priceSuggestionItems,
    },
    resolver: yupResolver(addPriceSuggestionPricesSchema(t)),
  });

  const updateItems = useCallback(
    (items: PriceSuggestionPriceItem[]) => {
      const formItems: AddPriceSuggestionPricesSchema['items'] = items.reduce(
        (prev, item) => ({
          ...prev,
          [item.mgSupplierItemId + '']: {
            salesPrice: item.price ?? null,
            fromDate: item.validFrom ?? new Date(),
            toDate: item.validTo,
          } as PriceSuggestionPricesSchema,
        }),
        {}
      );
      setValue('items', formItems);
    },
    [setValue]
  );

  const updateItem = useCallback(
    (itemForUpdate: PriceSuggestionPriceItem) => {
      const updatedItems = priceSuggestionPriceItems.map((item) =>
        item.mgSupplierItemId === itemForUpdate.mgSupplierItemId ? itemForUpdate : item
      );
      updateItems(updatedItems);
      setPriceSuggestionPriceItems(updatedItems);
    },
    [priceSuggestionPriceItems, setPriceSuggestionPriceItems, updateItems]
  );

  const columns: GridColDef<PriceSuggestionPriceItem>[] = useMemo(
    () => [
      {
        ...baseColumnProps,
        field: 'mgItem',
        headerName: t('calculus.itemsAffected.columns.itemName'),
        width: 260,
        renderCell: ({ row }) => (
          <StackedTextCell topLine={row.primaryText} bottomLine={row.mgItemNumber} />
        ),
      },
      {
        ...baseColumnProps,
        field: 'supplier',
        headerName: t('calculus.level.supplier'),
        width: 260,
        renderCell: ({ row }) => (
          <StackedTextCell topLine={row.mgSupplierName} bottomLine={row.mgSupplierId} />
        ),
      },
      {
        ...baseColumnProps,
        field: 'validFrom',
        headerName: t('myAssortment.columns.validFrom'),
        renderCell: ({ row }) => (
          <DateSelectField
            value={row.validFrom}
            maxDate={row.validTo}
            setNewDate={(newDate: Date | null) =>
              newDate && updateItem({ ...row, validFrom: newDate })
            }
          />
        ),
      },
      {
        ...baseColumnProps,
        field: 'validTo',
        headerName: t('myAssortment.columns.validTo'),
        width: 200,
        renderCell: ({ row }) => (
          <DateSelectField
            clearable
            minDate={row.validFrom}
            value={row.validTo}
            setNewDate={(newDate: Date | null) =>
              updateItem({ ...row, validTo: newDate ?? undefined })
            }
          />
        ),
      },
      {
        ...baseColumnProps,
        field: 'price',
        headerName: t('myAssortment.columns.salesPrice'),
        renderCell: ({ row }) => {
          const error = errors.items?.[row.mgSupplierItemId]?.salesPrice;
          return (
            <TextField
              size="small"
              defaultValue={row.price}
              onChange={(e) => updateItem({ ...row, price: Number(e.target.value) })}
              error={!!error}
            />
          );
        },
      },
    ],
    [errors.items, t, updateItem]
  );

  return (
    <>
      <DialogContent
        className="padding-0"
        sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}
      >
        <Box sx={{ flexGrow: 1, position: 'relative' }}>
          <Box sx={{ position: 'absolute', inset: 0 }}>
            <DataGridPremium<PriceSuggestionPriceItem>
              rows={priceSuggestionPriceItems}
              columns={columns}
              getRowId={(row) => row.mgSupplierItemId}
              rowHeight={65}
              disableColumnMenu
              disableColumnReorder
              hideFooter
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={1}>
          <TextButton onClick={onClose}>{t('products.cancel')}</TextButton>

          {goBack && (
            <OutlinedButton color="secondary" size="small" onClick={goBack}>
              {t('common.goBack')}
            </OutlinedButton>
          )}

          <ContainedButton
            type="submit"
            color="secondary"
            size="small"
            onClick={handleSubmit(handleSubmitPrices)}
          >
            {t('myAssortment.actions.addItems')}
          </ContainedButton>
        </Stack>
      </DialogActions>
    </>
  );
}
